import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Search, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { EMISSION_CATEGORIES, getCategoryLabel } from '../lib/constants';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_RESULTS = 50;

// Search box + result table for picking one approved emission factor (same search as Custom Mapping).
export default function EmissionFactorSearch({ selectedEF, onSelect }) {
  const [category, setCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [text, setText] = useState('');
  const [efId, setEfId] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const matchesFilters = (ef) => {
    const subCategoryMatches = !subCategory || ef.sub_category?.toLowerCase().includes(subCategory.toLowerCase());
    const needle = text.toLowerCase();
    const textMatches = !text || [ef.sub_category, ef.type, ef.sub_type].some(v => v?.toLowerCase().includes(needle));
    return subCategoryMatches && textMatches;
  };

  const searchEmissionFactors = async () => {
    if (!category && !subCategory && !text && !efId) {
      toast.error('Please enter a search criteria (category, sub category, text, or EF ID)');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({ status: 'approved', page_size: '200' });
      if (category) params.append('category', category);
      const searchTerm = efId || text || subCategory;
      if (searchTerm) params.append('search', searchTerm);

      const response = await axios.get(`${API_URL}/emission-factors?${params}`);
      const items = response.data.items || [];
      const matches = efId ? items : items.filter(matchesFilters);

      setResults(matches.slice(0, MAX_RESULTS));
      if (matches.length === 0) toast.info('No emission factors found matching your criteria');
    } catch (error) {
      toast.error('Failed to search emission factors');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-gray-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="w-5 h-5" />
          Search & Select Emission Factor
        </CardTitle>
        <CardDescription>The conversion applies to the sub category, type and sub type of the emission factor you pick</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="space-y-2">
            <Label>Filter by Category</Label>
            <Select value={category || 'all'} onValueChange={(v) => setCategory(v === 'all' ? '' : v)}>
              <SelectTrigger data-testid="ef-search-category">
                <SelectValue placeholder="Any category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any category</SelectItem>
                {EMISSION_CATEGORIES.map(opt => (
                  <SelectItem key={opt.value} value={opt.label}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Filter by Sub Category</Label>
            <Input value={subCategory} onChange={(e) => setSubCategory(e.target.value)} placeholder="Sub category..." data-testid="ef-search-sub-category" />
          </div>
          <div className="space-y-2">
            <Label>Search by Text</Label>
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type, sub type..." data-testid="ef-search-text" />
          </div>
          <div className="space-y-2">
            <Label>Search by EF ID</Label>
            <Input value={efId} onChange={(e) => setEfId(e.target.value)} placeholder="Enter EF ID" data-testid="ef-search-id" />
          </div>
          <div className="space-y-2">
            <Label>&nbsp;</Label>
            <Button onClick={searchEmissionFactors} disabled={loading} className="w-full" data-testid="ef-search-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              Search
            </Button>
          </div>
        </div>

        {selectedEF && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="font-semibold text-green-800 flex items-center gap-2">
              <Check className="w-5 h-5" />
              Selected Emission Factor: #{selectedEF.emission_factor_id}
            </p>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-green-700">
              <div><strong>Category:</strong> {getCategoryLabel(selectedEF.category)}</div>
              <div><strong>Sub Category:</strong> {selectedEF.sub_category}</div>
              <div><strong>Type:</strong> {selectedEF.type || '—'}</div>
              <div><strong>Sub Type:</strong> {selectedEF.sub_type || '—'}</div>
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs">EF ID</TableHead>
                    <TableHead className="text-xs">Category</TableHead>
                    <TableHead className="text-xs">Sub Category</TableHead>
                    <TableHead className="text-xs">Type</TableHead>
                    <TableHead className="text-xs">Sub Type</TableHead>
                    <TableHead className="text-xs">Country</TableHead>
                    <TableHead className="text-xs">Input Unit</TableHead>
                    <TableHead className="text-xs">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((ef) => {
                    const isSelected = selectedEF?.emission_factor_id === ef.emission_factor_id;
                    return (
                      <TableRow key={ef.emission_factor_id} className={isSelected ? 'bg-green-50' : ''}>
                        <TableCell className="text-xs font-mono">{ef.emission_factor_id}</TableCell>
                        <TableCell className="text-xs">{getCategoryLabel(ef.category)}</TableCell>
                        <TableCell className="text-xs">{ef.sub_category}</TableCell>
                        <TableCell className="text-xs">{ef.type || '—'}</TableCell>
                        <TableCell className="text-xs">{ef.sub_type || '—'}</TableCell>
                        <TableCell className="text-xs">{ef.country}</TableCell>
                        <TableCell className="text-xs">{ef.input_unit}</TableCell>
                        <TableCell>
                          <Button size="sm" variant={isSelected ? 'default' : 'outline'} onClick={() => onSelect(ef)} data-testid={`select-ef-${ef.emission_factor_id}`}>
                            {isSelected ? 'Selected' : 'Select'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="p-2 bg-gray-50 text-xs text-muted-foreground border-t">
              Showing {results.length} results (max {MAX_RESULTS})
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
